import {
  useCandidateHeaderLazyQuery,
  useCompetencyMapHeaderLazyQuery,
  useExaminationTitleLazyQuery,
  useKnowledgeBlockHeaderLazyQuery,
  useTrialTitleLazyQuery,
  useVacancyHeaderLazyQuery,
} from "@generated/graphql";
import ROUTES from "constants/routes";
import { getTestingPath } from "helpers/index";
import qs from "qs";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BackPageButtonSearchParams, BackPageButtonValue } from "./types";

type GetBackPageButtonProps = (props: {
  defaultValue: BackPageButtonValue;
  getSearch?: () => string | undefined;
}) => BackPageButtonValue | null;

// eslint-disable-next-line import/prefer-default-export
export const useGetBackPageButtonProps: GetBackPageButtonProps = ({ defaultValue, getSearch }) => {
  const [searchParams] = useSearchParams();
  const params = useMemo(() => qs.parse(searchParams.toString()), [searchParams]);
  const { vacancyId, candidateId, examinationId, trialId, competencyMapId, competencyMapsList, knowledgeBlockId } =
    params as BackPageButtonSearchParams;

  const [title, setTitle] = useState<string | null>(null);

  const [fetchVacancyHeader, { loading: fetchVacancyHeaderLoading }] = useVacancyHeaderLazyQuery();

  const [fetchCandidateHeader, { loading: fetchCandidateHeaderLoading }] = useCandidateHeaderLazyQuery();

  const [fetchExaminationHeader, { loading: fetchExaminationHeaderLoading }] = useExaminationTitleLazyQuery();

  const [fetchTrialHeader, { loading: fetchTrialHeaderLoading }] = useTrialTitleLazyQuery();

  const [fetchCompetencyMapHeader, { loading: fetchCompetencyMapHeaderLoading }] = useCompetencyMapHeaderLazyQuery();

  const [fetchKnowledgeBlockHeader, { loading: fetchKnowledgeBlockpHeaderLoading }] =
    useKnowledgeBlockHeaderLazyQuery();

  const loading =
    fetchVacancyHeaderLoading ||
    fetchCandidateHeaderLoading ||
    fetchExaminationHeaderLoading ||
    fetchTrialHeaderLoading ||
    fetchCompetencyMapHeaderLoading ||
    fetchKnowledgeBlockpHeaderLoading;

  useEffect(() => {
    if (vacancyId) {
      fetchVacancyHeader({ variables: { vacancyId } }).then(({ data }) => {
        setTitle(data?.vacancy?.title || null);
      });
    } else if (candidateId) {
      fetchCandidateHeader({ variables: { candidateId } }).then(({ data }) => {
        setTitle(data?.companyCandidate?.fullName || null);
      });
    } else if (examinationId) {
      fetchExaminationHeader({ variables: { examinationId } }).then(({ data }) => {
        setTitle(data?.examination?.title || null);
      });
    } else if (trialId) {
      fetchTrialHeader({ variables: { trialId } }).then(({ data }) => {
        setTitle(data?.trial?.examination.title || null);
      });
    } else if (competencyMapId) {
      fetchCompetencyMapHeader({ variables: { competencyMapId } }).then(({ data }) => {
        setTitle(data?.competencyMap?.title || null);
      });
    } else if (competencyMapsList) {
      setTitle("Список карт компетенций");
    } else if (knowledgeBlockId) {
      fetchKnowledgeBlockHeader({ variables: { knowledgeBlockId } }).then(({ data }) => {
        setTitle(data?.knowledgeBlock?.title || null);
      });
    } else {
      setTitle(null);
    }
  }, [vacancyId, candidateId, examinationId, trialId, competencyMapId, competencyMapsList]);

  const value = useMemo(() => {
    if (loading) return null;

    if (!title) return defaultValue;

    if (vacancyId) {
      return {
        text: `Вакансия «${title}»`,
        link: {
          pathname: `/${ROUTES.VACANCIES}/view/${vacancyId}`,
          search: getSearch ? getSearch() : undefined,
        },
      };
    }
    if (candidateId) {
      return {
        text: `Кандидат ${title}`,
        link: {
          pathname: `/${ROUTES.CANDIDATE}/view/${candidateId}`,
          search: getSearch ? getSearch() : undefined,
        },
      };
    }
    if (examinationId || trialId) {
      return {
        text: `Тестирование «${title}»`,
        link: {
          pathname: getTestingPath(examinationId ? "examination" : "trial", examinationId || trialId),
          search: getSearch ? getSearch() : undefined,
        },
      };
    }
    if (competencyMapId) {
      return {
        text: `Карта компетенций «${title}»`,
        link: {
          pathname: `/${ROUTES.COMPETENCE_MAPS}/view/${competencyMapId}`,
          search: getSearch ? getSearch() : undefined,
        },
      };
    }
    if (competencyMapsList) {
      return {
        text: title,
        link: {
          pathname: `/${ROUTES.COMPETENCE_MAPS}`,
          search: getSearch ? getSearch() : undefined,
        },
      };
    }
    if (knowledgeBlockId) {
      return {
        text: `Блок знаний «${title}»`,
        link: {
          pathname: `/${ROUTES.KNOWLEDGE_BLOCKS}/view/${knowledgeBlockId}`,
          search: getSearch ? getSearch() : undefined,
        },
      };
    }

    return null;
  }, [
    title,
    loading,
    vacancyId,
    candidateId,
    examinationId,
    trialId,
    competencyMapId,
    competencyMapsList,
    defaultValue,
  ]);

  return value;
};
